import { useState, type FormEvent, type ReactNode } from "react";
import type { DocumentKind, JobStageTag, Technology } from "@sts/doc-catalogue";
import type { DocumentSearchQuery, DocumentSearchResult } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import {
  DOCUMENT_KINDS,
  JOB_STAGE_TAGS,
  KIND_LABEL,
  STAGE_TAG_LABEL,
  TECHNOLOGIES,
  TECHNOLOGY_LABEL,
} from "../lib/documents.js";

/**
 * "Which form do I need?"
 *
 * Installers do not know form codes; they know what they are trying to do. So
 * the box asks for the task in their own words — "the customer has moved" — and
 * the API answers with the document and, crucially, with the phrase that
 * matched. That explanation is the most useful thing on the screen: it is the
 * difference between a result somebody trusts and one they have to guess at,
 * and it is what makes the answer learnable rather than magic.
 *
 * The second thing this screen must do is tell the truth about what the
 * platform can produce. Two thirds of the catalogue is not built yet. Hiding
 * those entries would answer "which form do I need?" with silence, which is a
 * worse answer than "this one, and we cannot generate it for you".
 */

interface Filters {
  readonly q: string;
  readonly kind: DocumentKind | "";
  readonly stage: JobStageTag | "";
  readonly technology: Technology | "";
  readonly onlyAvailable: boolean;
}

const EMPTY: Filters = {
  q: "",
  kind: "",
  stage: "",
  technology: "",
  onlyAvailable: false,
};

export function DocumentFinder(): ReactNode {
  const { client } = usePortal();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [submitted, setSubmitted] = useState<DocumentSearchQuery | null>(null);

  // The serialised query is the dependency, not the object: a fresh object on
  // every render would re-fetch for ever.
  const key = submitted === null ? "" : JSON.stringify(submitted);

  const { state, reload } = useAsync<readonly DocumentSearchResult[] | null>(
    () => (submitted === null ? Promise.resolve(null) : client.searchDocuments(submitted)),
    [client, key],
  );

  function submit(event: FormEvent): void {
    event.preventDefault();
    setSubmitted({
      ...(filters.q.trim() === "" ? {} : { q: filters.q.trim() }),
      ...(filters.kind === "" ? {} : { kind: filters.kind }),
      ...(filters.stage === "" ? {} : { stage: filters.stage }),
      ...(filters.technology === "" ? {} : { technology: filters.technology }),
      ...(filters.onlyAvailable ? { onlyAvailable: true } : {}),
      limit: 20,
    });
  }

  return (
    <>
      <div className="page-header">
        <h1>Which document do I need?</h1>
        <p className="lede">
          Describe the job in your own words. Every answer says which phrase it
          matched, so you can tell a good match from a near miss.
        </p>
      </div>

      <section>
        <form className="stacked" onSubmit={submit}>
          <div className="field">
            <label htmlFor="doc-q">The task</label>
            <input
              id="doc-q"
              type="search"
              value={filters.q}
              placeholder="I want to change the address"
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            />
            <p className="hint small">
              Plain words beat form codes, but a code works too — the catalogue
              keeps the old ones as aliases.
            </p>
          </div>

          <div className="field">
            <label htmlFor="doc-kind">Kind</label>
            <select
              id="doc-kind"
              value={filters.kind}
              onChange={(e) =>
                setFilters({ ...filters, kind: e.target.value as DocumentKind | "" })
              }
            >
              <option value="">Any kind</option>
              {DOCUMENT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {KIND_LABEL[kind]}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="doc-stage">Stage</label>
            <select
              id="doc-stage"
              value={filters.stage}
              onChange={(e) =>
                setFilters({ ...filters, stage: e.target.value as JobStageTag | "" })
              }
            >
              <option value="">Any stage</option>
              {JOB_STAGE_TAGS.map((stage) => (
                <option key={stage} value={stage}>
                  {STAGE_TAG_LABEL[stage]}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="doc-technology">Technology</label>
            <select
              id="doc-technology"
              value={filters.technology}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  technology: e.target.value as Technology | "",
                })
              }
            >
              <option value="">Any technology</option>
              {TECHNOLOGIES.map((technology) => (
                <option key={technology} value={technology}>
                  {TECHNOLOGY_LABEL[technology]}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="doc-available">
              <input
                id="doc-available"
                type="checkbox"
                checked={filters.onlyAvailable}
                onChange={(e) =>
                  setFilters({ ...filters, onlyAvailable: e.target.checked })
                }
              />{" "}
              Hide documents this platform cannot produce
            </label>
            <p className="hint small">
              Off by default. Being told which form you need and that we cannot
              generate it is more use than an empty result.
            </p>
          </div>

          <button type="submit">Find the document</button>
        </form>
      </section>

      <Async state={state} what="the document catalogue" reload={reload}>
        {(results) =>
          results === null ? (
            <p className="empty">
              Nothing searched yet. An empty box with a filter set is a fair
              question too — it lists everything at that stage.
            </p>
          ) : (
            <Results results={results} />
          )
        }
      </Async>
    </>
  );
}

function Results({
  results,
}: {
  readonly results: readonly DocumentSearchResult[];
}): ReactNode {
  if (results.length === 0) {
    return (
      <p className="empty">
        Nothing in the catalogue matched. Try the task rather than the document
        — &ldquo;the customer has moved&rdquo; finds more than
        &ldquo;variation&rdquo;.
      </p>
    );
  }

  const unavailable = results.filter((r) => !r.available).length;

  return (
    <section>
      <h2>
        Answers <span className="count">{results.length}</span>
      </h2>
      <p className="muted small">
        Best match first.
        {unavailable > 0
          ? ` ${unavailable} of these ${unavailable === 1 ? "is" : "are"} not something the platform can produce yet.`
          : ""}
      </p>
      <ol className="stacked">
        {results.map((result) => (
          <Result key={result.id} result={result} />
        ))}
      </ol>
    </section>
  );
}

function Result({ result }: { readonly result: DocumentSearchResult }): ReactNode {
  return (
    <li className="room">
      <h3>
        {result.name}{" "}
        <span className={result.available ? "status-word" : "status-word status-critical"}>
          {result.available ? "Available" : "Not built yet"}
        </span>
      </h3>

      <p>{result.purpose}</p>

      <p className="small muted">
        {KIND_LABEL[result.kind] ?? result.kind} ·{" "}
        {STAGE_TAG_LABEL[result.stage] ?? result.stage}
        {result.standardsRef ? (
          <>
            {" · "}
            <span className="standard">{result.standardsRef}</span>
          </>
        ) : null}
      </p>

      {/* The API returns its own reasoning. Reprinting it verbatim is what lets
          somebody see that "moved house" reached "change the address" through
          the synonym list rather than by luck. */}
      {result.matchedOn.length > 0 ? (
        <p className="small">
          <strong>Matched on</strong>{" "}
          {result.matchedOn.map((phrase, index) => (
            <span key={phrase}>
              {index > 0 ? ", " : ""}
              <span className="prov prov-measured">{phrase}</span>
            </span>
          ))}
        </p>
      ) : (
        <p className="small muted">
          No task phrase matched: this came back on its name, its purpose or a
          filter rather than on something somebody would say.
        </p>
      )}

      {result.available ? null : (
        <p className="notice warn small">
          This platform cannot produce this document today. It is shown because
          it is still the document you need — obtain it another way.
        </p>
      )}

      {result.notes ? <p className="small muted">{result.notes}</p> : null}
    </li>
  );
}
